import { z } from 'zod'
import type { LineKind } from './catalog.js'

/**
 * Finance reporting, on a CASH basis.
 *
 * Revenue is recognised on the date a jobcard was marked PAID, not when it was
 * invoiced. That is what "cash flow" means: money actually in the till. Work
 * that has been invoiced but not yet paid is reported separately as
 * `outstanding` so it is visible without inflating income.
 *
 * Cost is attributed to the same period as the revenue it earned, which keeps a
 * month's profit self-consistent even though the parts may have been bought
 * earlier.
 */

export const financeQuerySchema = z.object({
  /** How many whole months to report, counting back from the current one. */
  months: z.coerce.number().int().min(1).max(60).default(12),
})
export type FinanceQuery = z.infer<typeof financeQuerySchema>

/** Revenue, cost and profit for one slice of the business. */
export interface FinanceBucket {
  /** Net of VAT. VAT is the government's money and is never revenue. */
  revenueMinor: number
  /** Our cost, summed only over lines that actually record one. */
  costMinor: number
  /** Payment processing fees, subtracted from gross profit. */
  paymentFeesMinor: number
  /** revenue - cost - paymentFees. */
  profitMinor: number
  /** Profit as a percentage of revenue, one decimal. 0 when revenue is 0. */
  marginPct: number
  /**
   * Share of this bucket's revenue whose lines record a cost, 0-100.
   *
   * Below 100 the profit figure is an overstatement, because a line with no
   * recorded cost contributes revenue and nothing to subtract. The UI must say
   * so rather than presenting the number as fact.
   */
  costCoveragePct: number
}

export interface FinancePeriod extends FinanceBucket {
  /** `YYYY-MM`. */
  period: string
  /** Gross cash actually received in the period, VAT included. */
  cashReceivedMinor: number
  /** The VAT portion of that cash, owed onward to the tax authority. */
  vatCollectedMinor: number
  /** Number of jobcards paid in the period. */
  jobcardCount: number
}

export interface FinanceSummary extends FinanceBucket {
  /** Gross cash received across the whole window, VAT included. */
  cashReceivedMinor: number
  vatCollectedMinor: number
  /** Invoiced but not yet paid, gross. Not counted as income anywhere. */
  outstandingMinor: number
  outstandingCount: number
  jobcardCount: number
}

export interface FinanceReport {
  /** Inclusive `YYYY-MM` bounds of the window. */
  from: string
  to: string
  summary: FinanceSummary
  /** One entry per month, oldest first, with empty months included as zeros. */
  series: FinancePeriod[]
  /** Parts / services / materials, always all three, in LINE_KINDS order. */
  byCategory: Array<FinanceBucket & { kind: LineKind }>
}

/** Profit as a percentage of revenue, one decimal place; 0 when revenue is 0. */
export function marginPercent(profitMinor: number, revenueMinor: number): number {
  if (revenueMinor === 0) return 0
  return Math.round((profitMinor / revenueMinor) * 1000) / 10
}
